// User Management Types based on API Documentation
import { IPagination } from './assets'

export enum Country {
  Poland = 'PL',
  Ukraine = 'UA',
  Estonia = 'EE',
  Latvia = 'LV',
  Lithuania = 'LT',
  Slovenia = 'SI',
  Slovakia = 'SK',
  CzechRepublic = 'CZ',
  Croatia = 'HR',
  Serbia = 'RS',
  Romania = 'RO',
  Hungary = 'HU'
}

export interface BrandDto {
  id: string
  name: string
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null
}

export interface Permission {
  id: string
  name: string
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null
}

export interface RolePermissionDto {
  id: string
  role: RoleDto
  permission: Permission
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null
}

export interface RoleDto {
  id: string
  name: string
  rolePermissions: RolePermissionDto[]
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null
}

export interface UserCountryPermissionDto {
  id: string
  country: Country
  user: UserDto
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null
}

export interface UserDto {
  id: string
  email: string
  firstName: string
  lastName: string
  role: RoleDto
  countryPermissions: UserCountryPermissionDto[]
  defaultCountry: Country
  cookiesFlag: boolean
  privacyPolicyFlag: boolean
  newsletterFlag: boolean
  permittedBrands: BrandDto[]
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null
}

// API Request/Response Types

export interface CreateUserRequest {
  email: string
  firstName: string
  lastName: string
  password?: string
  roleId?: string
  countries?: Country[]
  brandIds?: string[]
  defaultCountry?: Country
}

export interface UpdateUserRequest {
  firstName?: string
  lastName?: string
  cookiesFlag?: boolean
  privacyPolicyFlag?: boolean
  newsletterFlag?: boolean
  roleId?: string
}

export interface UpdateUserRoleRequest {
  roleId: string
}

export interface CreateRoleRequest {
  name: string
}

export interface UpdateRoleRequest {
  name: string
}

export interface CreateRolePermissionRequest {
  roleId: string
  permission: string
}

export interface BulkCreateRolePermissionsRequest {
  roleId: string
  permissions: string[]
}

export interface CreateUserCountryPermissionRequest {
  userId: string
  country: Country
}

export interface SetDefaultCountryRequest {
  country: Country
}

export interface AddBrandPermissionRequest {
  userId: string
  brandId: string
}

// Pagination and Query Types

export interface PaginationQuery {
  limit?: { page: number; take: number }
  order?: { by: string; dir: 'asc' | 'desc' }
  filter?: any
  filterByPart?: any
  filterArrays?: any
  additional?: any
  search?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  recordsTotal: number
  recordsFiltered: number
  page: number
  size: number
}

// State Types

export interface UserManagementState {
  users: {
    status: 'initial' | 'loading' | 'loaded' | 'failed'
    data: UserDto[]
    pagination: IPagination
  }
  roles: {
    status: 'initial' | 'loading' | 'loaded' | 'failed'
    data: RoleDto[]
  }
  permissions: {
    status: 'initial' | 'loading' | 'loaded' | 'failed'
    data: Permission[]
  }
  brands: {
    status: 'initial' | 'loading' | 'loaded' | 'failed'
    data: BrandDto[]
  }
  userCountryPermissions: {
    status: 'initial' | 'loading' | 'loaded' | 'failed'
    data: UserCountryPermissionDto[]
  }
  aiLimitStatus: {
    status: 'initial' | 'loading' | 'loaded' | 'failed'
    data: AILimitStatus | null
  }
  currentUser: UserDto | null
  error: string | null
}

// Form Types

export interface UserFormData {
  email: string
  firstName: string
  lastName: string
  password?: string
  roleId: string
  countries: Country[]
  brandIds: string[]
  defaultCountry: Country
  cookiesFlag: boolean
  privacyPolicyFlag: boolean
  newsletterFlag: boolean
}

export interface UserEditFormData {
  firstName: string
  lastName: string
  cookiesFlag: boolean
  privacyPolicyFlag: boolean
  newsletterFlag: boolean
  roleId: string
}

// AI Limits Types
export interface AILimitStatus {
  hasLimit: boolean
  type: 'user' | 'brand_country' | 'global'
  duration: 'monthly' | 'one_time'
  monthlyLimit?: number
  currentUsage: number
  remaining: number
  resetDate: string | null
  autoRenew?: boolean
  isRenewable: boolean
}

export interface CreateAILimitRequest {
  type: 'user' | 'brand_country' | 'global'
  operationType: 'translation' | 'image_text_generation' | 'asset_image_text_generation'
  limitValue: number
  duration: 'monthly' | 'one_time'
  userId: string
  autoRenew?: boolean
}

export interface CreateOneTimeAILimitRequest {
  type: 'user' | 'brand_country' | 'global'
  operationType: 'translation' | 'image_text_generation' | 'asset_image_text_generation'
  limitValue: number
  userId: string
}

export interface UpdateAILimitRequest {
  limitValue?: number
  isActive?: boolean
  autoRenew?: boolean
}

export interface AILimit {
  id: string
  type: 'user' | 'brand_country' | 'global'
  operationType: 'translation' | 'image_text_generation' | 'asset_image_text_generation'
  limitValue: number
  duration: 'monthly' | 'one_time'
  userId?: string
  brandId?: string
  countryCode?: string
  autoRenew?: boolean
  resetDate?: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}
